import { useTranslation } from "react-i18next";
import { PERMIT_TYPES, STATUS_IDS, USER_ROLES_IDS } from "../../../constants/common"
import { useLocation, useNavigate } from "react-router-dom";
import useAuthStore from "../../../store/Auth";
import { Button } from "../../../components/button";
import ApprovePermitModal from "../../../components/modal/ApprovePermitModal";
import RejectPermitModal from "../../../components/modal/RejectPermitModal";
import { useCallback, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { UseFormHandleSubmit } from "react-hook-form";
import { fileToBase64, formattedDateTime } from "../../../lib/functions";
import { useAddUpdatePCPermit } from "../../../hooks/permit/mutation";
import { APP_ROUTES } from "../../../routes";
import { Check, X } from "lucide-react";

interface FormData {
    employeeName: string;
    uploadEmployeeIDCard?: any;
    deviceType: string;
    serialNumber: string;
    building: string;
    permitStartDate: string;
    permitEndDate: string;
}

interface ActionButtonGroupProps {
    pcPermit?: {
        n_PermitLaptopID: number;
        n_ApprovalStatus: number;
        n_PermitNextApproverRoleID: number;
        permitEndDate: string;
    }
    handleSubmit: UseFormHandleSubmit<{
        uploadEmployeeIDCard?: any;
        employeeName: string;
        deviceType: string;
        serialNumber: string;
        building: string;
        permitStartDate: string;
        permitEndDate: string;
    }>
    isAccompanyPledge: boolean;
    isPermitApprovedByNextApprover: boolean;
    isFieldsDisabled: boolean;
}

const ActionButtonGroup = ({
    pcPermit,
    handleSubmit,
    isAccompanyPledge,
    isPermitApprovedByNextApprover,
    isFieldsDisabled
}: ActionButtonGroupProps) => {
    const { t } = useTranslation()

    const location = useLocation();

    const { user } = useAuthStore()

    const navigate = useNavigate()

    const searchParams = new URLSearchParams(location.search);

    const key = searchParams.get("ID");

    const {
        mutateAsync: AddUpdatePCPermit,
        isPending: isAddUpdatePCPermitLoading,
    } = useAddUpdatePCPermit();

    const onSubmit = useCallback(
        async (data: FormData) => {
            try {
                const body = {
                    n_PermitLaptopID: key ? Number(key) : 0,
                    n_UserID: Number(data.employeeName),
                    s_LaptopType: data.deviceType,
                    s_LaptopSerialNumber: data.serialNumber,
                    dt_PermitStartDate: formattedDateTime(new Date(data.permitStartDate)),
                    dt_PermitEndDate: formattedDateTime(new Date(data.permitEndDate)),
                    s_UserCardBase64: data.uploadEmployeeIDCard
                        ? await fileToBase64(data.uploadEmployeeIDCard)
                        : "",
                    s_UserCardContentType: data.uploadEmployeeIDCard
                        ? data.uploadEmployeeIDCard.type
                        : "",
                    s_Building: data.building,
                };

                await AddUpdatePCPermit(body);

                toast.success(
                    t(!key ? "permitRequestedSuccessfully" : "permitUpdatedSuccessfully")
                );

                setTimeout(() => {
                    navigate(APP_ROUTES.SEARCH_PERMIT_REQUEST);
                }, 700);
            } catch (error) {
                toast.error(t("somethingWentWrong")!);
            }
        },
        [AddUpdatePCPermit, key, navigate, t]
    );

    const [isApprovePermitModalOpen, setIsApprovePermitModalOpen] =
        useState(false);

    const isApproveDisabled = useMemo(
        () =>
            pcPermit?.n_ApprovalStatus === STATUS_IDS.EXPIRED ||
            pcPermit?.n_ApprovalStatus === STATUS_IDS.APPROVED ||
            pcPermit?.n_ApprovalStatus === STATUS_IDS.CANCELED ||
            pcPermit?.n_ApprovalStatus === STATUS_IDS.REJECTED ||
            // not fully approved -> only current approver can approve
            (pcPermit?.n_ApprovalStatus !== STATUS_IDS.APPROVED &&
                user?.n_RoleID !== pcPermit?.n_PermitNextApproverRoleID),
        [
            pcPermit?.n_ApprovalStatus,
            pcPermit?.n_PermitNextApproverRoleID,
            user?.n_RoleID,
        ]
    );

    const onApprove = useCallback(
        async (data: FormData) => {
            if (isApproveDisabled) return;

            if (new Date(data.permitEndDate) <= new Date()) {
                toast.error(t("permitExpiredCannotApprove"));
            } else if (
                new Date(data.permitEndDate) <= new Date(data.permitStartDate)
            ) {
                toast.error(t("permitDateError"));
            } else {
                setIsApprovePermitModalOpen(true);
            }
        },
        [isApproveDisabled, t]
    );

    const isRejectDisabled = useMemo(
        () =>
            pcPermit?.n_ApprovalStatus === STATUS_IDS.EXPIRED ||
            pcPermit?.n_ApprovalStatus === STATUS_IDS.CANCELED ||
            pcPermit?.n_ApprovalStatus === STATUS_IDS.REJECTED,
        [pcPermit?.n_ApprovalStatus]
    );

    const [isRejectAppointmentModalOpen, setIsRejectAppointmentModalOpen] =
        useState(false);

    const onReject = useCallback(async () => {
        if (isRejectDisabled) return;

        if (
            pcPermit?.permitEndDate &&
            new Date(pcPermit?.permitEndDate) <= new Date()
        ) {
            toast.error(t("permitExpiredCannotReject"));
            return;
        }

        setIsRejectAppointmentModalOpen(true);
    }, [isRejectDisabled, pcPermit?.permitEndDate, t]);

    const isApprovePermitPage = useMemo(
        () => location.pathname === APP_ROUTES.APPROVE_PC_IN_OUT_PERMIT,
        [location.pathname]
    );

    return (
        <>
            {user?.n_RoleID !== USER_ROLES_IDS.ADMIN && (
                <div className="flex justify-end gap-1">
                    {key && (
                        <>
                            {isApprovePermitPage ? (
                                <>
                                    <Button
                                        onClick={handleSubmit(onApprove)}
                                        Icon={<Check className="w-4" />}
                                        className="dark:bg-white dark:text-purple-primary hover:dark:text-purple-primary hover:dark:bg-white/80"
                                        disabled={
                                            user?.n_RoleID === USER_ROLES_IDS.STAFF ||
                                            isApproveDisabled
                                        }
                                    >
                                        {t("approve")}
                                    </Button>
                                    <Button
                                        type="button"
                                        onClick={onReject}
                                        className="bg-red-600 hover:bg-red-700"
                                        disabled={isRejectDisabled}
                                        Icon={<X className="w-4" />}
                                    >
                                        {t("reject")}
                                    </Button>
                                </>
                            ) : (
                                <Button
                                    onClick={handleSubmit(onSubmit)}
                                    isLoading={
                                        isAddUpdatePCPermitLoading
                                        //   isAddAttachmentLoading
                                    }
                                    disabled={
                                        !isAccompanyPledge ||
                                        isAddUpdatePCPermitLoading ||
                                        isPermitApprovedByNextApprover ||
                                        (user?.n_RoleID === USER_ROLES_IDS.STAFF &&
                                            isFieldsDisabled)
                                        // isSubmitDisabled ||
                                        // isVisitApprovedByNextApprover ||
                                    }
                                    className="dark:bg-white dark:text-purple-primary hover:dark:text-purple-primary hover:dark:bg-white/80"
                                >
                                    {t("update")}
                                </Button>
                            )}
                        </>
                    )}

                    {!key && (
                        <Button
                            onClick={handleSubmit(onSubmit)}
                            isLoading={isAddUpdatePCPermitLoading}
                            disabled={!isAccompanyPledge || isAddUpdatePCPermitLoading}
                            className="dark:bg-white dark:text-purple-primary hover:dark:text-purple-primary hover:dark:bg-white/80"
                        >
                            {t("submit")}
                        </Button>
                    )}
                </div>
            )}

            <ApprovePermitModal
                type={PERMIT_TYPES.PC_IN_OUT_PERMIT}
                permitId={pcPermit?.n_PermitLaptopID || 0}
                isOpen={isApprovePermitModalOpen}
                onOpenChange={() => setIsApprovePermitModalOpen(false)}
            />

            <RejectPermitModal
                type={PERMIT_TYPES.PC_IN_OUT_PERMIT}
                permitId={pcPermit?.n_PermitLaptopID || 0}
                isOpen={isRejectAppointmentModalOpen}
                onOpenChange={() => setIsRejectAppointmentModalOpen(false)}
            />
        </>
    )
}

export default ActionButtonGroup
